'use client';

import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import { ReactNode, useEffect } from 'react';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Mirror the theme SETTING into the SHARED `owlcat-theme` cookie so the central
 * accounts app inherits the user's light/dark choice on redirect — exactly like
 * the shared `owlcat-locale` cookie does for language. We write the SETTING
 * (`theme`: system|light|dark), NOT resolvedTheme: a 'system' user must CLEAR the
 * cookie so sibling apps also fall back to the OS instead of being pinned to a
 * concrete light/dark that stops tracking prefers-color-scheme cross-app. The
 * parent domain is env-driven (NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN); host-only when
 * unset (bare localhost dev), Domain-scoped behind Caddy. The pre-hydration seed
 * script in app/layout.tsx reads the cookie back into localStorage['theme'].
 */
function ThemeCookieSync() {
  const { theme } = useTheme();
  useEffect(() => {
    const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim();
    const host = window.location.hostname;
    const onParent = !!parent && (host === parent || host.endsWith(`.${parent}`));
    const domain = onParent ? `; Domain=.${parent}` : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const write = (name: string, value: string) => {
      document.cookie = `${name}=${value}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
    };
    // Mirror the theme SETTING so central accounts (+ sibling apps) inherit it.
    if (theme === 'light' || theme === 'dark') write('owlcat-theme', theme);
    else if (theme === 'system')
      document.cookie = `owlcat-theme=; Path=/; Max-Age=0; SameSite=Lax${domain}${secure}`;
    // Mirror the active locale (from the /<locale>/ path prefix) into the shared
    // owlcat-locale cookie so cross-app screens (accounts profile) match language.
    const loc = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1];
    if (loc && ['ru', 'en', 'cs', 'uk', 'de', 'fr'].includes(loc)) write('owlcat-locale', loc);
  }, [theme]);
  return null;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <ThemeCookieSync />
      {children}
    </NextThemesProvider>
  );
}
